<template>
  <div class="card tm-tickets-card mb-4" :class="{ 'is-collapsed': isCollapsed }">
    <div 
      class="card-header border-0 bg-transparent pt-4 pb-2 px-4 cursor-pointer" 
      @click="isCollapsed = !isCollapsed"
    >
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <div class="icon-box-header me-3" :class="{ 'bg-primary': !isCollapsed, 'bg-secondary': isCollapsed }">
            <i class="fas fa-ticket-alt text-white"></i>
          </div>
          <div>
            <h6 class="mb-0 fw-bold text-dark opacity-75 text-uppercase letter-spacing-1">
              Tickets em Aberto
            </h6>
            <span class="text-primary fw-bold small">
              {{ tickets.length }} registro(s) pendente(s)
            </span>
          </div>
        </div>
        <div class="collapse-icon">
          <i class="fas" :class="isCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
        </div>
      </div>
    </div>

    <transition name="collapse">
      <div v-show="!isCollapsed" class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-4">Criado em</th>
                <th>Cliente</th>
                <th>Atividade</th>
                <th>Classificação</th>
                <th>Observação</th>
                <th>Status</th>
                <th class="text-end pe-4">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="ticket in tickets"
                :key="ticket.id"
                :class="{ 'row-active': ticket.id === activeTicketId }"
              >
                <td class="ps-4 text-muted small">{{ formatDateBrToView(ticket.created_at) }}</td>
                <td><span class="fw-bold text-dark">{{ ticket.customer_name }}</span></td>
                <td><span class="badge bg-soft-primary text-primary">{{ ticket.task_name }}</span></td>
                <td><span class="badge bg-soft-secondary text-secondary">{{ ticket.task_type_name }}</span></td>
                <td>
                  <div class="note-truncate" :title="ticket.note">
                    {{ ticket.note || '---' }}
                  </div>
                </td>
                <td>
                  <span class="badge" :class="statusBadgeClass(ticket.status)">
                    {{ renameStatus(ticket.status) }}
                  </span>
                </td>
                <td class="text-end pe-4">
                  <div class="d-flex justify-content-end gap-1">
                    <span v-if="ticket.is_fixed_value" class="badge bg-warning text-dark d-flex align-items-center">
                      <i class="fas fa-lock me-1"></i> FIXO
                    </span>
                    
                    <template v-if="!ticket.is_fixed_value && (ticket.status === 'pending' || ticket.status === 'in_progress')">
                      <button
                        class="btn btn-sm btn-icon"
                        :class="ticket.status === 'in_progress' ? 'btn-outline-info' : 'btn-primary'"
                        :title="ticket.status === 'in_progress' ? 'Adicionar Atividade' : 'Iniciar Atendimento'"
                        @click="$emit('start', ticket)"
                      >
                        <i class="fas" :class="ticket.status === 'in_progress' ? 'fa-plus' : 'fa-play'"></i>
                      </button>
                    </template>

                    <button
                      v-if="ticket.is_fixed_value && ticket.status === 'pending'"
                      class="btn btn-sm btn-primary btn-icon"
                      title="Iniciar Ticket Fixo"
                      @click="$emit('start-fixed', ticket.id)"
                    >
                      <i class="fas fa-play"></i>
                    </button>

                    <button
                      v-if="ticket.status === 'in_progress'"
                      class="btn btn-sm btn-success btn-icon"
                      title="Finalizar Atendimento"
                      @click="$emit('finish', ticket.id)"
                    >
                      <i class="fas fa-check"></i>
                    </button>

                    <button
                      v-if="ticket.status === 'in_progress'"
                      class="btn btn-sm btn-outline-primary btn-icon"
                      title="Registrar Despesas"
                      @click="$emit('expense', ticket)"
                    >
                      <i class="fas fa-dollar-sign"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'OpenTicketsPanel',
  props: {
    tickets: Array,
    activeTicketId: { type: [Number, Boolean, String], default: null },
  },
  data() {
    return {
      isCollapsed: true
    }
  },
  methods: {
    formatDateBrToView(originalDate) {
      if (!originalDate) return '---'
      const data = new Date(originalDate)
      return data.toLocaleDateString("pt-BR")
    },
    renameStatus(status) {
      const map = {
        'pending': 'Pendente',
        'in_progress': 'Em Aberto',
        'completed': 'Finalizado'
      }
      return map[status] || status
    },
    statusBadgeClass(status) {
      const map = {
        'pending': 'bg-soft-warning text-warning',
        'in_progress': 'bg-soft-info text-info',
        'completed': 'bg-soft-success text-success'
      }
      return map[status] || 'bg-light text-dark'
    }
  }
}
</script>

<style scoped lang="sass">
.tm-tickets-card
  border: none
  border-radius: 24px
  background: white
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05)
  overflow: hidden
  transition: all 0.3s ease

.cursor-pointer
  cursor: pointer

.icon-box-header
  width: 38px
  height: 38px
  border-radius: 10px
  display: flex
  align-items: center
  justify-content: center
  transition: all 0.3s ease
  &.bg-primary
    background: linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)
    box-shadow: 0 4px 10px rgba(9, 132, 227, 0.3)
  &.bg-secondary
    background: #b2bec3
    box-shadow: none

.letter-spacing-1
  letter-spacing: 1px

thead th
  background: #f8f9fa
  border-bottom: 2px solid #f1f2f6
  color: #a4b0be
  font-weight: 800
  font-size: 0.7rem
  text-transform: uppercase
  letter-spacing: 1px
  padding-top: 12px
  padding-bottom: 12px

tbody tr
  transition: all 0.2s
  &:hover
    background-color: #fcfcfd
  &.row-active
    background-color: #fff9e6
    border-left: 4px solid #f1c40f

.note-truncate
  max-width: 250px
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis
  font-size: 0.85rem
  color: #636e72

.badge
  border-radius: 8px
  padding: 6px 10px
  font-weight: 700
  font-size: 0.7rem

.bg-soft-primary
  background: rgba(9, 132, 227, 0.1)
.bg-soft-secondary
  background: rgba(108, 117, 125, 0.1)
.bg-soft-warning
  background: rgba(241, 196, 15, 0.1)
.bg-soft-info
  background: rgba(0, 206, 201, 0.1)
.bg-soft-success
  background: rgba(0, 184, 148, 0.1)

.btn-icon
  width: 32px
  height: 32px
  padding: 0
  display: flex
  align-items: center
  justify-content: center
  border-radius: 8px
  transition: all 0.2s
  &:hover
    transform: scale(1.1)

.collapse-icon
  color: #b2bec3
  font-size: 0.8rem

.collapse-enter-active, .collapse-leave-active
  transition: max-height 0.3s ease, opacity 0.3s ease
  max-height: 1000px
  overflow: hidden

.collapse-enter, .collapse-leave-to
  max-height: 0
  opacity: 0
</style>
